import { c as _c } from "react/compiler-runtime";
import * as React from "react";
import { cn } from "../utils";
import { Icon } from "../icon";
import { tagVariants } from "./tag.variants";
import type { TagProps } from "./tag.types";

/**
 * Tag Component
 *
 * V&A Waterfront Design System - Tag Component
 * Figma: https://www.figma.com/design/CV2ePEn9S0SSZ7T3AAPqF4/V-A-Waterfront---Website---Foundation-Design-System?node-id=10817-103331
 *
 * Non-interactive categorization labels and status indicators.
 *
 * @example
 * // Basic usage
 * <Tag>Category</Tag>
 *
 * // Different styles
 * <Tag variant="dark">Dark Tag</Tag>
 * <Tag variant="transparent-dark">Overlay Tag</Tag>
 *
 * // Sizes
 * <Tag size="sm">Small</Tag>
 * <Tag size="md">Medium</Tag>
 *
 * // With icons
 * <Tag iconLeft="circle">With Left Icon</Tag>
 * <Tag iconRight="chevron-right">With Right Icon</Tag>
 * <Tag iconLeft="map-pin" iconRight="chevron-right">Both Icons</Tag>
 *
 * // App section tags
 * <Tag variant="app-section" status="my-va">My V&A</Tag>
 * <Tag variant="app-section" status="deals">Deals</Tag>
 *
 * // Deal status tags
 * <Tag variant="deal-status" status="redeemed">Redeemed</Tag>
 * <Tag variant="deal-status" status="expired">Expired</Tag>
 */
const Tag = React.forwardRef<HTMLSpanElement, TagProps>((t0, ref) => {
  const $ = _c(27);
  let children;
  let className;
  let iconLeft;
  let iconRight;
  let props;
  let size;
  let status;
  let variant;
  if ($[0] !== t0) {
    ({
      className,
      variant,
      size,
      status,
      iconLeft,
      iconRight,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = iconLeft;
    $[4] = iconRight;
    $[5] = props;
    $[6] = size;
    $[7] = status;
    $[8] = variant;
  } else {
    children = $[1];
    className = $[2];
    iconLeft = $[3];
    iconRight = $[4];
    props = $[5];
    size = $[6];
    status = $[7];
    variant = $[8];
  }
  const iconSize = size === "sm" ? "size-3" : "size-4";
  let t1;
  if ($[9] !== className || $[10] !== size || $[11] !== status || $[12] !== variant) {
    t1 = cn(tagVariants({
      variant,
      size,
      status,
      className
    }));
    $[9] = className;
    $[10] = size;
    $[11] = status;
    $[12] = variant;
    $[13] = t1;
  } else {
    t1 = $[13];
  }
  let t2;
  if ($[14] !== iconLeft || $[15] !== iconSize) {
    t2 = iconLeft && <Icon name={iconLeft} className={iconSize} />;
    $[14] = iconLeft;
    $[15] = iconSize;
    $[16] = t2;
  } else {
    t2 = $[16];
  }
  let t3;
  if ($[17] !== iconRight || $[18] !== iconSize) {
    t3 = iconRight && <Icon name={iconRight} className={iconSize} />;
    $[17] = iconRight;
    $[18] = iconSize;
    $[19] = t3;
  } else {
    t3 = $[19];
  }
  let t4;
  if ($[20] !== children || $[21] !== props || $[22] !== ref || $[23] !== t1 || $[24] !== t2 || $[25] !== t3) {
    t4 = <span ref={ref} data-slot="tag" className={t1} {...props}>{t2}{children}{t3}</span>;
    $[20] = children;
    $[21] = props;
    $[22] = ref;
    $[23] = t1;
    $[24] = t2;
    $[25] = t3;
    $[26] = t4;
  } else {
    t4 = $[26];
  }
  return t4;
});
Tag.displayName = "Tag";
export { Tag };